/**
 * Read-only main-panel view for git-managed platform skills.
 * Same MarkdownEditor reader as documents (760px column, source/preview, page width).
 * Not a DB document - no save, no edit. Git-to-DB browse is Skills as Documents (LATER).
 */
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { DocumentContentStatusBar } from '@/components/features/document-content-status-bar'
import { MarkdownEditor } from '@/components/features/markdown-editor'
import { SkillDocumentBadge } from '@/components/features/skills/skill-document-badge'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'
import { useTheme } from '@/providers/theme-provider'

export function PlatformSkillPreview({
  slug,
  fallbackName,
  fallbackCategory,
}: {
  slug: string
  fallbackName?: string
  fallbackCategory?: string
}) {
  const { resolvedTheme } = useTheme()
  const [body, setBody] = useState<string | null>(null)
  const [name, setName] = useState(fallbackName ?? slug)
  const [category, setCategory] = useState(fallbackCategory)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'wysiwyg' | 'source'>('wysiwyg')
  const [pageWidthMode, setPageWidthMode] = useState<'default' | 'full'>('default')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setBody(null)
    void (async () => {
      try {
        const res = await fetchWithAuth(`/api/skill/${encodeURIComponent(slug)}`)
        const data = await res.json()
        if (cancelled) return
        if (!data.success) {
          setError(data.error || 'Failed to load skill')
          return
        }
        setBody(typeof data.skill?.body === 'string' ? data.skill.body : '')
        if (typeof data.skill?.name === 'string' && data.skill.name.trim()) {
          setName(data.skill.name)
        }
        if (typeof data.skill?.category === 'string') {
          setCategory(data.skill.category)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Request failed')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug])

  const lineCount = (body || '').split('\n').length
  const charCount = (body || '').length

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col" data-testid={testIds.skills.platformPreview}>
      <SkillDocumentBadge tier="platform" category={category} />
      {loading ? (
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading {name}...
        </div>
      ) : error ? (
        <p className="px-6 py-8 text-sm text-destructive">{error}</p>
      ) : (
        <>
          <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
            <MarkdownEditor
              key={slug}
              initialContent={body || ''}
              readOnly
              darkMode={resolvedTheme === 'dark'}
              className="h-full"
              documentTitle={name}
              viewMode={viewMode}
              pageWidthMode={pageWidthMode}
            />
          </div>
          <DocumentContentStatusBar
            viewMode={viewMode}
            wysiwygEditorMounted={false}
            localIsDeleted={false}
            pageWidthMode={pageWidthMode}
            showPageWidthToggle
            showEditToggle={false}
            onSetViewMode={setViewMode}
            onSetWysiwygEditorMounted={() => {}}
            onTogglePageWidthMode={() =>
              setPageWidthMode((mode) => (mode === 'default' ? 'full' : 'default'))
            }
            localSaving={false}
            hasUnsavedChanges={false}
            lastSaved={null}
            lastFileSave={null}
            deletedMessage={null}
            onRestore={() => {}}
            contentLineCount={lineCount}
            contentCharCount={charCount}
            error={null}
            infoTips="Platform skill - git-managed and read-only. Workspace and personal skills open as editable documents."
          />
        </>
      )}
    </div>
  )
}
